import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS, BRAND_NAME } from "@/lib/site";
import logoDark from "@/assets/logo-dark-trimmed.png";
import logoIcon from "@/assets/logo-icon-dark.png";

const STRATEGY_PATH = "/strategy-call";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const { pathname } = useLocation();
  const isActive = (href) => href === "/" ? pathname === "/" : (pathname === href || pathname.startsWith(`${href}/`));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    window.dispatchEvent(new CustomEvent("lr-menu", { detail: { open } }));
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const close = () => { setOpen(false); setExpanded(null); };
  const linkCls = "relative text-[0.78rem] font-medium tracking-wide text-legacy/80 hover:text-legacy transition-colors duration-300";

  return (
    <>
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-ivory/95 backdrop-blur-md border-b ${
        scrolled ? "border-gold/40 shadow-[0_1px_30px_-20px_rgba(17,17,17,0.5)]" : "border-gold/20 shadow-[0_1px_20px_-22px_rgba(17,17,17,0.4)]"
      }`}
    >
      <div className="container-lr flex items-center justify-between h-[92px] md:h-[100px]">
        <Link to="/" data-testid="header-logo-link" className="flex items-center shrink-0">
          <img src={logoDark} alt={BRAND_NAME} className="hidden sm:block h-[52px] md:h-[60px] w-auto object-contain" />
          <img src={logoIcon} alt={BRAND_NAME} className="sm:hidden h-14 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-3 xl:gap-5">
          {NAV_LINKS.map((link) => {
            const testid = `nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`;
            if (link.children) {
              return (
                <div key={link.label} className="relative group">
                  <Link to={link.href} data-testid={testid} className={`${linkCls} inline-flex items-center gap-1`}>
                    {link.label}
                    <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                  </Link>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200">
                    <div className="w-64 rounded-card border border-gold/30 bg-ivory shadow-[0_24px_50px_-20px_rgba(17,17,17,0.4)] p-2">
                      {link.children.map((c) => (
                        <Link key={c.label} to={c.href} data-testid={`nav-sub-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="block rounded-lg px-3 py-2.5 text-[0.82rem] text-legacy/80 hover:text-legacy hover:bg-taupe/15 transition-colors">
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <Link key={link.label} to={link.href} data-testid={testid} className={`${linkCls} after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-gold after:transition-all after:duration-300 hover:after:w-full`}>
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center shrink-0">
          <Link to={STRATEGY_PATH} data-testid="header-cta-button" className="btn-base btn-primary whitespace-nowrap text-[0.8rem] px-5 py-3">
            Book Your Free Strategy Call
          </Link>
        </div>

        <button type="button" aria-label="Open menu" data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-button text-legacy hover:text-bronze transition-colors">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
    </header>

      <div data-testid="mobile-menu" className={`lg:hidden fixed inset-x-0 top-[92px] md:top-[100px] bottom-0 z-40 bg-legacy transition-opacity duration-300 ${open ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"}`}>
        <div className="container-lr flex flex-col pt-6 pb-16 h-full overflow-y-auto overscroll-contain">
          <span className="gold-rule mb-3" />
          <nav className="flex flex-col divide-y divide-white/10">
            {NAV_LINKS.map((link) => {
              const testid = `mobile-nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`;
              const activeCls = isActive(link.href) ? "text-gold" : "text-ivory hover:text-gold focus-visible:text-gold";
              if (link.children) {
                const isOpen = expanded === link.label;
                return (
                  <div key={link.label} className="py-1">
                    <div className="flex items-center justify-between">
                      <Link to={link.href} onClick={close} data-testid={testid} className={`font-serif text-2xl py-4 transition-colors duration-200 ${activeCls}`}>{link.label}</Link>
                      <button type="button" aria-label={`Toggle ${link.label}`} aria-expanded={isOpen} onClick={() => setExpanded(isOpen ? null : link.label)} className="p-3 -mr-1 text-gold">
                        <ChevronDown size={22} className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    {isOpen && (
                      <div className="pb-4 pl-1 flex flex-col">
                        {link.children.map((c) => (
                          <Link key={c.label} to={c.href} onClick={close} data-testid={`mobile-nav-sub-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`text-base py-3 transition-colors duration-200 ${isActive(c.href) ? "text-gold" : "text-ivory/75 hover:text-gold focus-visible:text-gold"}`}>{c.label}</Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link key={link.label} to={link.href} onClick={close} data-testid={testid} className={`font-serif text-2xl py-4 transition-colors duration-200 ${activeCls}`}>{link.label}</Link>
              );
            })}
          </nav>
          <Link to={STRATEGY_PATH} onClick={close} data-testid="mobile-cta-button" className="btn-base btn-gold mt-8 w-full">Book Your Free Strategy Call</Link>
        </div>
      </div>
    </>
  );
};
